import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { CodeableConcept, Dosage, MedicationRequest, MedicationStatement } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { getMedicationDosageText } from '../../utils/medicationFormatting';
import { getMedicationDosage, getMedicationTitleParts, type MedicationListRecord } from '@liza/fhir-utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader, ItemTitle } from '../ui/item';

export type MedicationDetailProps = {
  medication: WithId<MedicationRequest | MedicationStatement>;
  onBack?: () => void;
};

/** The medication's name, preferring the same split the care plan renders so it reads the same on
 * both. Falls back to the concept text (or a `medicationReference` display, which carries no
 * structured names) when there is nothing composable. */
const getMedicationName = (medication: MedicationListRecord, fallback: string): string =>
  getMedicationTitleParts(medication)?.name ||
  (medication.medicationCodeableConcept && formatCodeableConcept(medication.medicationCodeableConcept)) ||
  medication.medicationReference?.display ||
  fallback;

const formatOptionalDate = (value: string | undefined, fallback: string): string =>
  value ? format(parseISO(value), DATE_FORMATS.DATE) : fallback;

const frequencyText = (dosage: Dosage | undefined): string | undefined => {
  const timingCodeText = dosage?.timing?.code?.text;
  if (timingCodeText) {
    return timingCodeText;
  }

  const repeat = dosage?.timing?.repeat;
  if (!repeat) {
    return undefined;
  }

  if (repeat.frequency && repeat.period && repeat.periodUnit) {
    const unitMap: Record<string, string> = {
      d: 'day',
      wk: 'week',
      mo: 'month',
      h: 'hour',
      min: 'minute',
    };
    const unit = unitMap[repeat.periodUnit] ?? repeat.periodUnit;
    const unitLabel = repeat.period === 1 ? unit : `${unit}s`;
    return `${repeat.frequency}x every ${repeat.period} ${unitLabel}`;
  }

  if (repeat.frequency) {
    return `${repeat.frequency}x`;
  }

  return undefined;
};

const firstCategoryConcept = (
  category: CodeableConcept | CodeableConcept[] | undefined,
): CodeableConcept | undefined => {
  if (!category) {
    return undefined;
  }
  return Array.isArray(category) ? category[0] : category;
};

type MetadataRow = { title: string; value: string };

const formatCategoryValue = (
  medication: WithId<MedicationRequest | MedicationStatement>,
  t: TFunction<'records'>,
): string => {
  const concept = firstCategoryConcept(medication.category);
  return concept ? formatCodeableConcept(concept) : t('unknown');
};

const getRecordedOrPrescribedRow = (
  medication: WithId<MedicationRequest | MedicationStatement>,
  t: TFunction<'records'>,
): MetadataRow => {
  if (medication.resourceType === 'MedicationRequest') {
    return {
      title: t('prescribed'),
      value: formatOptionalDate(medication.authoredOn, t('unknown')),
    };
  }

  const recordedSource = medication.dateAsserted ?? medication.effectiveDateTime ?? medication.effectivePeriod?.start;

  return {
    title: t('recordedDate'),
    value: formatOptionalDate(recordedSource, t('unknown')),
  };
};

const getDosageBoundsFromTiming = (
  dosage: Dosage | undefined,
): { periodStart: string | undefined; periodEnd: string | undefined } => ({
  periodStart: dosage?.timing?.repeat?.boundsPeriod?.start,
  periodEnd: dosage?.timing?.repeat?.boundsPeriod?.end,
});

const mergeStatementEffectivePeriod = (
  medication: WithId<MedicationStatement>,
  periodStart: string | undefined,
  periodEnd: string | undefined,
): { rangeStart: string | undefined; rangeEnd: string | undefined } => ({
  rangeStart: medication.effectivePeriod?.start ?? periodStart,
  rangeEnd: medication.effectivePeriod?.end ?? periodEnd,
});

const buildMetadataItems = (
  medication: WithId<MedicationRequest | MedicationStatement>,
  t: TFunction<'records'>,
): MetadataRow[] => {
  const dosage = getMedicationDosage(medication);
  const { periodStart, periodEnd } = getDosageBoundsFromTiming(dosage);

  const { rangeStart, rangeEnd } =
    medication.resourceType === 'MedicationStatement'
      ? mergeStatementEffectivePeriod(medication, periodStart, periodEnd)
      : { rangeStart: periodStart, rangeEnd: periodEnd };

  const routeValue = dosage?.route ? formatCodeableConcept(dosage.route) : t('unknown');
  const dosageValue = getMedicationDosageText(dosage) ?? t('unknown');
  const frequencyValue = frequencyText(dosage) ?? t('unknown');

  return [
    { title: t('dosage'), value: dosageValue },
    { title: t('frequency'), value: frequencyValue },
    { title: t('route'), value: routeValue },
    { title: t('category'), value: formatCategoryValue(medication, t) },
    getRecordedOrPrescribedRow(medication, t),
    { title: t('start'), value: formatOptionalDate(rangeStart, '-') },
    { title: t('end'), value: formatOptionalDate(rangeEnd, '-') },
    { title: t('additionalInstructions'), value: medication.note?.map((note) => note.text).join('\n') ?? '-' },
  ];
};

export const MedicationDetail = ({ medication, onBack }: MedicationDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const name = getMedicationName(medication, t('unknownMedication'));
  const strengthForm = getMedicationTitleParts(medication)?.subtitle;
  const items = buildMetadataItems(medication, t as TFunction<'records'>);

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {onBack && (
        <Button
          id="back-to-medications-button"
          variant="ghost"
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToMedications')}</span>
        </Button>
      )}

      <Item>
        <ItemHeader className="flex flex-col items-start gap-2">
          <div className="flex justify-center gap-2">
            <Badge>{medication.status ?? t('unknown')}</Badge>
          </div>

          <ItemTitle className="text-lg font-medium" data-testid="medication-name">
            {name}
          </ItemTitle>
          {strengthForm && (
            <p className="text-sm text-muted-foreground" data-testid="medication-subtitle">
              {strengthForm}
            </p>
          )}
        </ItemHeader>
        <ItemContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {items.map((item) => (
              <div key={item.title}>
                <div className="mb-1 text-muted-foreground">{item.title}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>
        </ItemContent>
      </Item>
    </div>
  );
};
